import { useEffect, useState } from "react";
import { ClipboardList, MessageSquare, Pill, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "elara:hideAsyncExplainer";

const steps = [
  {
    icon: ClipboardList,
    title: "Submit your intake",
    description: "Health profile + goals",
  },
  {
    icon: MessageSquare,
    title: "Message your provider",
    description: "Secure chat, reply on your time",
  },
  {
    icon: Pill,
    title: "Get your plan",
    description: "Prescription sent if appropriate",
  },
];

export default function AsyncConsultExplainer() {
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    try {
      setHidden(localStorage.getItem(STORAGE_KEY) === "1");
    } catch {
      setHidden(false);
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignore
    }
    setHidden(true);
  };

  if (hidden) return null;

  return (
    <Card className="relative border-primary/20 bg-primary/5">
      <CardContent className="p-4 sm:p-5">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Dismiss explainer"
          onClick={dismiss}
          className="absolute right-2 top-2 h-7 w-7 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>

        <h3 className="text-sm font-semibold text-foreground mb-3 pr-8">
          How consultations work
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-3">
          {steps.map(({ icon: Icon, title, description }, idx) => (
            <div key={title} className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground leading-tight">
                  <span className="text-primary mr-1">{idx + 1}.</span>
                  {title}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground border-t border-primary/10 pt-3">
          Elara consultations happen by secure message, not video. Your provider
          will reply within 24 hours, so there are no appointments to join. For
          emergencies, call{" "}
          <a href="tel:911" className="font-medium text-foreground underline">
            911
          </a>
          .
        </p>
      </CardContent>
    </Card>
  );
}
